<script lang="ts" setup>
import { ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useHistoryPage } from '../composables/useHistoryPage'
import type { HistoryCommonConfig, HistoryDeleteModalTheme } from '../configs/types'
import HistoryEditOverlay from './HistoryEditOverlay.vue'
import HistoryTabPanel from './HistoryTabPanel.vue'
import HistoryTopBar from './HistoryTopBar.vue'
import { resources } from '@/config/brands'

const props = defineProps<{
  config: HistoryCommonConfig
  deleteModalTheme: HistoryDeleteModalTheme
}>()

const coverPlaceholder = resources.bookCoverPlaceholder
const infiniteRef = ref<HTMLElement>()

const {
  from,
  activeTab,
  editMode,
  selectedIds,
  deleteConfirmVisible,
  library,
  readHistory,
  isAllSelected,
  showTabbar,
  loadCurrent,
  switchTab,
  enterEdit,
  exitEdit,
  toggleSelectAll,
  openDeleteConfirm,
  closeDeleteConfirm,
  confirmDelete,
  onItemClick,
  onCoverError,
  toBookRowView,
} = useHistoryPage(props.config, coverPlaceholder)

useInfiniteScroll(infiniteRef, async () => {
  await loadCurrent()
}, { distance: 50 })
</script>

<template>
  <div
    class="novelyn-history-page h-100dvh flex flex-col overflow-hidden bg-n-bg-global"
    :class="showTabbar ? 'pb-20' : 'pb-24'"
  >
    <div v-if="from" class="flex-shrink-0">
      <BasicNavbar />
      <div class="h-5" />
    </div>

    <div class="novelyn-history-top-bar flex-shrink-0">
      <HistoryTopBar
        :active-tab="activeTab"
        :edit-mode="editMode"
        @switch-tab="switchTab"
        @enter-edit="enterEdit"
        @exit-edit="exitEdit"
      />
    </div>

    <div
      ref="infiniteRef"
      class="novelyn-history-card flex-1 overflow-auto mx-10px mb-10px rounded-8px bg-n-bg-card shadow-[0px_2px_10px_0px_rgba(220,224,228,0.4)] px-10px pt-16px pb-16px"
    >
      <HistoryTabPanel
        v-if="activeTab === 'library'"
        tab="library"
        :items="library.items"
        :fetching="library.fetching"
        :ending="library.ending"
        :page="library.page"
        :edit-mode="editMode"
        :selected-ids="selectedIds"
        :config="config"
        :cover-placeholder="coverPlaceholder"
        :to-book-row-view="toBookRowView"
        @item-click="onItemClick"
        @cover-error="onCoverError"
      />

      <HistoryTabPanel
        v-else
        tab="history"
        :items="readHistory.items"
        :fetching="readHistory.fetching"
        :ending="readHistory.ending"
        :page="readHistory.page"
        :edit-mode="editMode"
        :selected-ids="selectedIds"
        :config="config"
        :cover-placeholder="coverPlaceholder"
        :to-book-row-view="toBookRowView"
        @item-click="onItemClick"
        @cover-error="onCoverError"
      />
    </div>

    <HistoryEditOverlay
      :edit-mode="editMode"
      :is-all-selected="isAllSelected"
      :selected-count="selectedIds.size"
      :delete-confirm-visible="deleteConfirmVisible"
      :delete-modal-theme="deleteModalTheme"
      @toggle-all="toggleSelectAll"
      @request-delete="openDeleteConfirm"
      @close-delete="closeDeleteConfirm"
      @confirm-delete="confirmDelete"
    />

    <BasicTabbar v-if="showTabbar" tab="history" />
  </div>
</template>

<style lang="scss" scoped>
.novelyn-history-page {
  background: var(--novelyn-bg);
}

.novelyn-history-top-bar {
  height: 57px;
}

.novelyn-history-card {
  margin: 0 16px 12px;
  padding: 16px 14px;
  border-radius: 20px;
  box-shadow: var(--novelyn-shadow-sm);

  :deep(.border-b) {
    border-color: var(--novelyn-line);
  }
}

.novelyn-history-page :deep(.history-edit-bottom > div) {
  border-radius: 0 !important;
  box-shadow: none !important;
}
</style>
